/**
 * Pure helpers for YouTube URLs. Used by the Team Live external-stream form
 * (see docs/superpowers/specs/2026-08-23-team-live-external-streams-design.md).
 */

/** YouTube video IDs are exactly 11 chars of [A-Za-z0-9_-]. */
const VIDEO_ID = /^[\w-]{11}$/;

function isVideoId(value: string | null | undefined): value is string {
  return !!value && VIDEO_ID.test(value);
}

/**
 * Extract an 11-character YouTube video ID from a pasted URL
 * (watch / live / embed / shorts / youtu.be, with or without protocol,
 * www/m host, extra params) or a raw ID. Returns null when no video ID
 * is recognizable.
 */
export function parseYouTubeVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (isVideoId(trimmed)) return trimmed;

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^(www|m|music)\./, "");
  if (host === "youtu.be") {
    return isVideoId(url.pathname.split("/").filter(Boolean)[0])
      ? url.pathname.split("/").filter(Boolean)[0]
      : null;
  }
  if (host !== "youtube.com" && host !== "youtube-nocookie.com") return null;

  const v = url.searchParams.get("v");
  if (isVideoId(v)) return v;

  const parts = url.pathname.split("/").filter(Boolean);
  if (["live", "embed", "shorts"].includes(parts[0] ?? "")) {
    return isVideoId(parts[1]) ? parts[1] : null;
  }
  return null;
}
